import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, LayoutDashboard } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import { portfolio, visionBenefits, sharedEcosystem } from "@/data/brand";

const VisionPage = () => (
  <main data-testid="vision-page" className="bg-[#0a0908]">
    {/* Hero */}
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-24 grain">
      <div className="absolute inset-0 bg-gradient-to-b from-[#161412] to-[#0a0908]" />
      <div className="relative z-10 max-w-5xl mx-auto text-center px-6 py-20">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="text-[10px] md:text-xs tracking-[0.45em] uppercase text-[#c5a059] mb-8">
          The Bigger Vision
        </motion.p>
        <h1 className="font-serif text-white font-light leading-[1.05] text-3xl sm:text-5xl md:text-6xl lg:text-7xl">
          <MaskLines lines={["One Hospitality Family."]} delay={0.4} />
          <MaskLines lines={["Distinct Brands."]} delay={0.55} lineClassName="italic text-[#c5a059]" />
          <MaskLines lines={["One Elevated Digital Standard."]} delay={0.7} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1 }}
          className="text-white/50 font-light text-lg mt-10 max-w-2xl mx-auto leading-relaxed">
          River Stone is the flagship of a scalable, multi-brand website system — a foundation designed to power an entire hospitality portfolio.
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="mt-14">
          <button
            type="button"
            data-testid="vision-scroll-down"
            aria-label="Scroll to content"
            onClick={() => {
              const el = document.getElementById("vision-diagram");
              if (el && window.lenis) window.lenis.scrollTo(el, { offset: -80 });
              else el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="text-[#c5a059] hover:text-white transition-colors cursor-pointer p-2"
          >
            <ArrowDown className="mx-auto animate-bounce" size={22} />
          </button>
        </motion.div>
      </div>
    </section>

    {/* Diagram */}
    <section id="vision-diagram" className="py-24 md:py-32 border-y border-white/10">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <div className="inline-block border border-[#c5a059] px-10 py-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#c5a059]">The Parent</p>
            <p className="font-serif text-3xl md:text-4xl text-white mt-2">Pack Brothers Hospitality</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}><ArrowDown className="mx-auto my-8 text-[#c5a059]/50" size={28} /></Reveal>
        <Reveal delay={0.15}>
          <p className="font-serif text-2xl md:text-3xl text-white/80 italic">8 Distinct Brands</p>
        </Reveal>
        <Reveal delay={0.2}><ArrowDown className="mx-auto my-8 text-[#c5a059]/50" size={28} /></Reveal>
        <Reveal delay={0.25}>
          <div className="inline-block bg-[#c5a059] px-10 py-6">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#0a0908]/70">The Foundation</p>
            <p className="font-serif text-3xl md:text-4xl text-[#0a0908] mt-2">One Shared Digital Ecosystem</p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* 8 Properties */}
    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="text-center mb-16">
          <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">The Portfolio</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light mt-5">
            <MaskLinesInView lines={["Eight properties. One system."]} />
          </h2>
          <p className="text-white/50 font-light mt-4 max-w-xl mx-auto">Each brand retains its own identity — colour, type and personality — atop shared infrastructure.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {portfolio.map((b, i) => (
            <Reveal key={b.name} delay={(i % 4) * 0.08}>
              <div className="group relative border border-white/10 h-full overflow-hidden transition-colors duration-500 hover:border-white/30 flex flex-col" data-testid={`portfolio-${i}`}>
                <div className="absolute top-0 left-0 h-1 w-full transition-all duration-500 z-10" style={{ backgroundColor: b.accent }} />
                {/* Logo tile */}
                <div className="relative bg-white h-32 flex items-center justify-center p-6">
                  <img src={b.logo} alt={b.name} style={{ maxHeight: `${b.logoMax || 64}px` }} className="w-auto object-contain" />
                  {b.live && <span className="absolute top-3 right-3 text-[9px] tracking-[0.2em] uppercase text-white bg-[#C1272D] px-2 py-1">Flagship</span>}
                </div>
                <div className="p-6 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl" style={{ color: b.accent }}>{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-serif text-xl text-white leading-tight">{b.name}</h3>
                  </div>
                  <p className="text-[11px] tracking-[0.15em] uppercase mt-3" style={{ color: b.accent }}>{b.type}</p>
                  <p className="text-white/40 text-sm font-light mt-1">{b.city}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Shared ecosystem chips */}
    <section className="py-20 border-y border-white/10 bg-[#161412] grain relative">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059] mb-8">Shared beneath every brand</p>
          <div className="flex flex-wrap justify-center gap-3">
            {sharedEcosystem.map((s, i) => (
              <motion.span key={s}
                initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="text-xs tracking-[0.15em] uppercase text-white/70 border border-white/15 px-4 py-2.5">
                {s}
              </motion.span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* 4 Benefits */}
    <section className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {visionBenefits.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.1} className="bg-[#0a0908]">
            <div className="p-9 h-full">
              <span className="font-serif text-5xl text-[#c5a059]/40">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-serif text-2xl text-white mt-5">{b.title}</h3>
              <p className="text-white/50 text-sm font-light mt-4 leading-relaxed">{b.copy}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Corporate Console / Portal */}
    <section className="py-24 md:py-32 border-t border-white/10 bg-[#161412] grain relative">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">
            <LayoutDashboard size={14} /> The Command Center
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light mt-6 leading-[1.05]">
            <MaskLinesInView lines={["One portal for", "the entire portfolio."]} />
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="text-white/50 font-light mt-6 max-w-2xl mx-auto leading-relaxed">
            Behind the eight brands sits the Pack Brothers Console — a single dashboard where each store manager sees their own restaurant, and corporate sees the whole group: reservations, inquiries, performance and more, all in one place.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <a
            href="https://packbrothersconsole.vzngfx.com/"
            target="_blank"
            rel="noreferrer"
            data-testid="vision-console-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-[#c5a059] text-[#0a0908] px-9 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase hover:bg-[#b08d4a] transition-colors duration-300"
          >
            Open the Pack Brothers Console
            <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </Reveal>
      </div>
    </section>

    {/* Final statement */}
    <section className="py-28 md:py-40 border-t border-white/10 text-center grain relative">
      <div className="max-w-4xl mx-auto px-6">
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white font-light leading-[1.1]">
            <MaskLinesInView lines={["The goal isn't just", "a better website."]} />
          </h2>
          <p className="font-serif italic text-2xl sm:text-3xl md:text-4xl text-[#c5a059] mt-8 leading-snug">
            It&rsquo;s a better digital experience across the entire hospitality portfolio.
          </p>
        </Reveal>
      </div>
    </section>
  </main>
);

export default VisionPage;
